import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { fetchApi } from '../api';
import { enrichProjectMetadata, getEnrichmentQuality, type EnrichmentQuality } from '../deep42';
import type {
  ProjectDetails,
  ProjectRepository,
  ProjectContractsResponse,
  ProjectNews,
  RepoFilters,
  Pagination,
} from '../../types/project';

/**
 * Conservative enrichment logic - only enrich if metadata is truly incomplete
 *
 * Enrichment criteria (ANY of these triggers enrichment):
 * 1. Missing critical metadata (no website OR no Twitter)
 * 2. Very low data coverage (< 5 repos AND no contracts)
 * 3. Empty project (no social links at all)
 *
 * For high-quality projects like ETH, BTC, SOL - we should NOT auto-enrich!
 */
function shouldEnrichProject(data: ProjectDetails): boolean {
  const { project, stats } = data;

  // Check 1: Missing BOTH website and Twitter (critical!)
  const hasCriticalMetadata = project.websiteUrl || project.twitterUrl;
  if (!hasCriticalMetadata) {
    return true; // No social links at all - definitely needs enrichment
  }

  // Check 2: Has ONE social link but missing the other + has very little data
  const missingWebsite = !project.websiteUrl;
  const missingTwitter = !project.twitterUrl;
  const hasLowDataCoverage = stats.totalRepos < 5 && stats.totalContracts === 0;

  if ((missingWebsite || missingTwitter) && hasLowDataCoverage) {
    return true; // Incomplete metadata + low coverage - needs enrichment
  }

  // Check 3: No contracts at all (might be a new project needing discovery)
  // BUT only if also missing social links
  if (stats.totalContracts === 0 && (!project.websiteUrl && !project.twitterUrl)) {
    return true;
  }

  // Otherwise: Data looks complete, skip enrichment
  return false;
}

// Hook to fetch project details and stats
// Automatically enriches data in the background via Deep42 API (ONLY if needed!)
export function useProjectDetails(symbol: string) {
  const [enrichmentQuality, setEnrichmentQuality] = useState<EnrichmentQuality | null>(null);
  const [isEnriching, setIsEnriching] = useState(false);

  const query = useQuery({
    queryKey: ['project', symbol],
    queryFn: () => fetchApi<ProjectDetails>(`/api/projects/${symbol}`),
    enabled: !!symbol,
  });

  // Enrich project data in background ONLY if metadata is incomplete
  useEffect(() => {
    if (!query.data || isEnriching) return;

    // Check if enrichment is needed
    const needsEnrichment = shouldEnrichProject(query.data);

    if (!needsEnrichment) {
      console.log('[Project Enrichment] Skipped - data is complete', {
        symbol,
        hasWebsite: !!query.data.project.websiteUrl,
        hasTwitter: !!query.data.project.twitterUrl,
        totalRepos: query.data.stats.totalRepos,
        totalContracts: query.data.stats.totalContracts,
      });
      return;
    }

    const enrichData = async () => {
      setIsEnriching(true);

      console.log('[Project Enrichment] Starting - incomplete metadata detected', {
        symbol,
        missingWebsite: !query.data.project.websiteUrl,
        missingTwitter: !query.data.project.twitterUrl,
        lowRepoCount: query.data.stats.totalRepos < 5,
        lowContractCount: query.data.stats.totalContracts === 0,
      });

      try {
        // Call Deep42 search-projects with ALL known metadata
        const result = await enrichProjectMetadata({
          symbol: query.data.project.symbol,
          name: query.data.project.name,
          websiteUrl: query.data.project.websiteUrl,
          twitterUrl: query.data.project.twitterUrl,
          // Note: contracts will be fetched separately by useProjectContracts
          // We could fetch them here too, but keeping hooks independent for now
        });

        // Extract quality metrics
        const quality = getEnrichmentQuality(result);
        setEnrichmentQuality(quality);

        console.log('[Project Enrichment] Complete', {
          symbol,
          quality,
        });
      } catch (error) {
        console.warn('[Project Enrichment] Failed (non-critical):', error);
      } finally {
        setIsEnriching(false);
      }
    };

    // Run enrichment in background (non-blocking)
    enrichData();
  }, [query.data, symbol, isEnriching]);

  return {
    ...query,
    enrichmentQuality,
    isEnriching,
  };
}

// Hook to fetch project repositories with filters
export function useProjectRepos(symbol: string, filters: RepoFilters = {}) {
  const queryParams = new URLSearchParams();

  if (filters.language) queryParams.append('language', filters.language);
  if (filters.minStars) queryParams.append('minStars', filters.minStars.toString());
  if (filters.minContracts) queryParams.append('minContracts', filters.minContracts.toString());
  if (filters.sort) queryParams.append('sort', filters.sort);
  if (filters.order) queryParams.append('order', filters.order);
  if (filters.limit) queryParams.append('limit', filters.limit.toString());
  if (filters.offset) queryParams.append('offset', filters.offset.toString());

  return useQuery({
    queryKey: ['project-repos', symbol, filters],
    queryFn: () =>
      fetchApi<{ repositories: ProjectRepository[]; pagination: Pagination }>(
        `/api/projects/${symbol}/repos?${queryParams.toString()}`
      ),
    enabled: !!symbol,
  });
}

// Hook to fetch project contracts grouped by chain
export function useProjectContracts(symbol: string) {
  return useQuery({
    queryKey: ['project-contracts', symbol],
    queryFn: () => fetchApi<ProjectContractsResponse>(`/api/projects/${symbol}/contracts`),
    enabled: !!symbol,
  });
}

// Hook to fetch project news stories
export function useProjectNews(symbol: string, limit = 20, offset = 0) {
  return useQuery({
    queryKey: ['project-news', symbol, limit, offset],
    queryFn: () =>
      fetchApi<{ stories: ProjectNews[]; pagination: Pagination }>(
        `/api/projects/${symbol}/news?limit=${limit}&offset=${offset}`
      ),
    enabled: !!symbol,
  });
}
